// ============================================
// Calibration Comparison Engine
//
// Compares blind run output (Claude) against
// Dr. C's SOAP assessment. Adapts the existing
// 9-point comparison framework for calibration.
//
// Reuses: DECISION_AREAS, computeAlignmentScore,
// generateComparisonSummary from comparisonEngine.ts
// ============================================

import type { DecisionPoint } from '../../types/clinicalCalibration';
import { DECISION_AREAS } from '../../types/clinicalCalibration';
import {
  computeAlignmentScore,
  generateComparisonSummary,
  CLAUDE_PROXY_URL,
} from '../patientJourney/comparisonEngine';
import type {
  BlindRunOutput,
  DrCAssessment,
  ExtractedBiomarker,
} from '../../types/labUpload';

/** Build the system prompt for calibration comparison. */
function buildCalibrationSystemPrompt(): string {
  return `You are a clinical calibration engine for a functional medicine platform. Your job is to compare a blind AI analysis against a physician's SOAP-derived assessment and produce structured decision point comparisons.

You MUST respond with ONLY a JSON array — no explanation, no markdown outside the JSON fence. Use this exact format:

\`\`\`json
[
  {
    "area": "Primary Dx",
    "drCPosition": "...",
    "medgemmaPosition": "...",
    "alignment": "aligned" | "delta" | "both_missed",
    "deltaNote": "..."
  }
]
\`\`\`

NOTE: The "medgemmaPosition" field contains the AI blind run analysis (not MedGemma specifically).

Rules for determining alignment:
- "aligned": Both identified substantially the same finding, even if worded differently.
- "delta": One identified something the other missed, or they disagree on interpretation.
- "both_missed": Neither addressed an area that clinical evidence suggests should have been caught given the labs.

For deltaNote:
- If aligned, set deltaNote to null.
- If delta, explain the specific divergence concisely (1-2 sentences).
- If both_missed, explain what was missed and why it matters.

Be clinically precise. Do not inflate alignment — only mark "aligned" when positions genuinely converge.`;
}

/** Build the user prompt for calibration comparison. */
function buildCalibrationUserPrompt(
  blindRun: BlindRunOutput,
  drCAssessment: DrCAssessment,
  biomarkers: ExtractedBiomarker[],
): string {
  const areasDescription = DECISION_AREAS.map(
    (da) => `${da.area} (weight: ${da.clinicalWeight})`,
  ).join('\n  ');

  const biomarkerSummary = biomarkers
    .filter((b) => b.value !== null)
    .map((b) => `- ${b.rawName}: ${b.value} ${b.unit}`)
    .join('\n');

  const rootCauseSummary = blindRun.rootCauses
    .map((rc) => `- ${rc.rootCause} (confidence: ${rc.confidence}): ${rc.clinicalReasoning}`)
    .join('\n');

  return `Compare the AI blind run analysis against Dr. C's SOAP assessment across the 9 standard decision areas.

== LAB RESULTS ==
${biomarkerSummary}

== AI BLIND RUN ANALYSIS ==
Diagnosis: ${blindRun.diagnosis}
Protocol: ${blindRun.protocol}
Recommended Products: ${blindRun.recommendedProducts.join(', ') || '(none)'}
Root Causes:
${rootCauseSummary}
${blindRun.followUpTimeline ? `Follow-up Timeline: ${blindRun.followUpTimeline}` : ''}
${blindRun.contraindications?.length ? `Contraindications: ${blindRun.contraindications.join(', ')}` : ''}

== DR. C'S ASSESSMENT (from SOAP) ==
Diagnosis: ${drCAssessment.diagnosis}
Products: ${drCAssessment.products.join(', ') || '(none)'}
Lab Orders: ${drCAssessment.labOrders.join(', ') || '(none)'}
Notes: ${drCAssessment.notes || '(none)'}

== DECISION AREAS TO EVALUATE ==
  ${areasDescription}

For each of the 9 areas, extract what Dr. C's position is (from SOAP), what the AI's position is (from blind run), and whether they are aligned, divergent (delta), or both missed something.

Return ONLY the JSON array with exactly 9 objects, one per area, in the order listed above.`;
}

/**
 * Compare a blind run analysis against Dr. C's SOAP assessment.
 * Returns 9 decision points with alignment scores.
 */
export async function compareBlindRunToDrC(
  blindRun: BlindRunOutput,
  drCAssessment: DrCAssessment,
  biomarkers: ExtractedBiomarker[],
): Promise<{ decisionPoints: DecisionPoint[]; alignmentScore: number; summary: string }> {
  const systemPrompt = buildCalibrationSystemPrompt();
  const userPrompt = buildCalibrationUserPrompt(blindRun, drCAssessment, biomarkers);

  const response = await fetch(CLAUDE_PROXY_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: 'claude-sonnet-4-5-20250929',
      max_tokens: 2000,
      messages: [{ role: 'user', content: userPrompt }],
      system: systemPrompt,
    }),
  });

  if (!response.ok) {
    const errText = await response.text();
    throw new Error(`Claude proxy returned ${response.status}: ${errText.slice(0, 200)}`);
  }

  const data = await response.json();
  const rawText: string = data.content?.[0]?.text ?? '';

  if (!rawText) {
    throw new Error('Claude proxy returned empty content');
  }

  // Parse JSON from response
  const fenced = rawText.match(/```json\s*([\s\S]*?)```/);
  const jsonText = fenced ? fenced[1].trim() : rawText;

  const start = jsonText.search(/[\[{]/);
  if (start === -1) throw new Error('No JSON found in comparison response');

  const open = jsonText[start];
  const close = open === '[' ? ']' : '}';
  let depth = 0;
  let jsonStr = '';
  for (let i = start; i < jsonText.length; i++) {
    if (jsonText[i] === open) depth++;
    if (jsonText[i] === close) depth--;
    if (depth === 0) {
      jsonStr = jsonText.slice(start, i + 1);
      break;
    }
  }

  const rawPoints = JSON.parse(jsonStr) as Array<{
    area: string;
    drCPosition: string;
    medgemmaPosition: string;
    alignment: 'aligned' | 'delta' | 'both_missed';
    deltaNote?: string | null;
  }>;

  // Map to typed DecisionPoints with clinical weights
  const weightMap = new Map(
    DECISION_AREAS.map((da) => [da.area, da.clinicalWeight]),
  );

  const decisionPoints: DecisionPoint[] = rawPoints.map((rp) => ({
    area: rp.area,
    drCPosition: rp.drCPosition,
    medgemmaPosition: rp.medgemmaPosition,
    alignment: rp.alignment,
    deltaNote: rp.deltaNote ?? undefined,
    clinicalWeight: weightMap.get(rp.area) ?? 0,
  }));

  // Ensure all 9 canonical areas are present
  for (const da of DECISION_AREAS) {
    if (!decisionPoints.find((dp) => dp.area === da.area)) {
      decisionPoints.push({
        area: da.area,
        drCPosition: 'Not assessed',
        medgemmaPosition: 'Not assessed',
        alignment: 'both_missed',
        deltaNote: 'This area was not evaluated in the comparison.',
        clinicalWeight: da.clinicalWeight,
      });
    }
  }

  const alignmentScore = computeAlignmentScore(decisionPoints);
  const { summary } = generateComparisonSummary(decisionPoints);

  return { decisionPoints, alignmentScore, summary };
}
